if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.selectors = {
          viewer: '[id^="GalleryViewer"]',
          thumbnails: '[id^="GalleryThumbnails"]',
          mediaList: '[id^="Slider-Gallery"]',
          mediaItems: ['.product__media-item'],
        };

        this.sliderInstance = false;
        this.thumbsInstance = false;
        this.videoSliderInstance = false;
        this.videoModuleBound = false;
        this.galleryMode = 'images';
        this.activeVideoId = '';
      }

      connectedCallback() {
        // this.init();
        FoxTheme.Motion.inView(this, this.init.bind(this));
      }

      init() {
        this.elements = window.FoxTheme.utils.queryDomNodes(this.selectors, this);
        this.mediaLayout = this.dataset.mediaLayout;
        this.galleryMediaStyle = this.dataset.galleryMediaStyle || 'framed';
        this.onlyImage = this.dataset.onlyImage === 'true';
        this.enableDesktopSlider = this.dataset.enableDesktopSlider === 'true';
        this.enableMobileThumbnails = this.dataset.enableMobileThumbnails === 'true';
        this.enableImageZoom = this.dataset.enableImageZoom === 'true';
        this.setSliderOptions();

        const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        mql.onchange = this.updateMediaLayout.bind(this);
        this.updateMediaLayout();

        if (this.enableImageZoom) {
          this.initImageZoom();
        }

        this.bindVideoModule();
      }

      bindVideoModule() {
        this.videoLayer = this.querySelector('[data-gallery-video-layer]');
        this.videoSwiperEl = this.querySelector('[data-gallery-video-swiper]');
        this.galleryMode = this.dataset.galleryMode || 'images';
        this.activeVideoId = this.dataset.activeVideoId || this.dataset.featuredVideoId || '';

        if (!this.videoLayer) return;

        this.setActiveVideo(this.activeVideoId, false);

        if (!this.videoModuleBound) {
          this.videoModuleBound = true;

          this.addEventListener('click', (event) => {
            const trigger = event.target.closest('[data-gallery-mode-trigger]');
            if (!trigger || !this.contains(trigger)) return;
            event.preventDefault();
            this.setGalleryMode(
              trigger.getAttribute('data-gallery-mode-trigger'),
              trigger.getAttribute('data-video-id')
            );
          });

          if (this.elements.thumbnails) {
            this.elements.thumbnails.addEventListener('click', () => {
              this.setGalleryMode('images');
            });
          }
        }

        if (this.galleryMode === 'video') {
          this.initVideoSlider();
        }

        this.updateRailPreview();
      }

      initVideoSlider() {
        if (typeof this.videoSliderInstance === 'object' || !this.videoSwiperEl) return;
        if (!this.videoSwiperEl.classList.contains('swiper')) return;

        this.videoSliderInstance = new window.FoxTheme.Carousel(this.videoSwiperEl, {
          slidesPerView: 1,
          spaceBetween: 0,
          loop: false,
          observer: true,
          observeParents: true,
          threshold: 8,
          touchStartPreventDefault: false,
          navigation: {
            nextEl: this.videoLayer.querySelector('.product__media-gallery-video-next'),
            prevEl: this.videoLayer.querySelector('.product__media-gallery-video-prev'),
          },
          pagination: {
            el: this.videoLayer.querySelector('.product__media-gallery-video-pagination'),
            type: 'fraction',
            clickable: true,
          },
        });
        this.videoSliderInstance.init();

        this.videoSliderInstance.slider.on('slideChange', (swiper) => {
          const slide = swiper.slides[swiper.activeIndex];
          if (!slide) return;
          const videoId = slide.getAttribute('data-video-id');
          if (videoId === this.activeVideoId) return;
          this.setActiveVideo(videoId);
          if (this.galleryMode === 'video') this.playActiveVideo();
        });
      }

      setGalleryMode(mode, videoId) {
        if (!this.videoLayer || !mode) return;

        if (mode === 'video') {
          this.initVideoSlider();
          this.setActiveVideo(videoId);
          if (this.videoSliderInstance && this.videoSliderInstance.slider) {
            this.videoSliderInstance.slider.update();
          }
        }

        if (this.galleryMode === mode) {
          if (mode === 'video') {
            this.playActiveVideo();
            this.updateRailPreview();
            this.syncVideoTriggers();
          }
          return;
        }

        this.galleryMode = mode;
        this.dataset.galleryMode = mode;

        const isVideo = mode === 'video';
        this.videoLayer.hidden = !isVideo;
        this.videoLayer.setAttribute('aria-hidden', isVideo ? 'false' : 'true');
        this.syncVideoTriggers();

        if (isVideo) {
          this.toggleSliderDraggableState(false);
          if (this.videoSliderInstance && this.videoSliderInstance.slider) {
            this.videoSliderInstance.slider.update();
          }
          this.playActiveVideo();
        } else {
          this.pauseAllGalleryVideos();
          if (this.sliderInstance && this.sliderInstance.slider) {
            const { slides, activeIndex } = this.sliderInstance.slider;
            const isModelMediaType = slides[activeIndex] && slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          } else {
            this.toggleSliderDraggableState(true);
          }
        }

        this.updateRailPreview();
      }

      setActiveVideo(videoId, pauseOthers = true) {
        const items = [...this.querySelectorAll('[data-gallery-video-item]')];
        if (!items.length) return;

        let target = videoId
          ? items.find((item) => item.getAttribute('data-video-id') === String(videoId))
          : null;
        if (!target) {
          target =
            items.find((item) => item.getAttribute('data-video-id') === String(this.activeVideoId)) || items[0];
        }

        this.activeVideoId = target.getAttribute('data-video-id') || '';
        this.dataset.activeVideoId = this.activeVideoId;
        this.videoElement = target.querySelector('video-element');

        items.forEach((item) => {
          if (pauseOthers && item !== target) {
            const videoEl = item.querySelector('video-element');
            if (videoEl && typeof videoEl.pause === 'function') videoEl.pause();
          }
        });

        if (this.videoSliderInstance && this.videoSliderInstance.slider) {
          const index = items.indexOf(target);
          if (index >= 0 && this.videoSliderInstance.slider.activeIndex !== index) {
            this.videoSliderInstance.slider.slideTo(index, 0);
          }
        }
      }

      syncVideoTriggers() {
        this.querySelectorAll('[data-gallery-mode-trigger]').forEach((trigger) => {
          const triggerMode = trigger.getAttribute('data-gallery-mode-trigger');
          const triggerVideoId = trigger.getAttribute('data-video-id');
          let active = triggerMode === this.galleryMode;

          if (triggerMode === 'video' && triggerVideoId) {
            active = this.galleryMode === 'video' && triggerVideoId === this.activeVideoId;
          }

          trigger.classList.toggle('is-active', active);
          trigger.closest('.product__video-rail-btn')?.classList.toggle('is-active', active);

          if (trigger.getAttribute('role') === 'tab') {
            trigger.setAttribute('aria-selected', triggerMode === this.galleryMode ? 'true' : 'false');
          }
          if (trigger.hasAttribute('aria-pressed')) {
            trigger.setAttribute('aria-pressed', active ? 'true' : 'false');
          }
        });
      }

      playActiveVideo() {
        if (!this.videoElement || typeof this.videoElement.play !== 'function') return;
        this.videoElement.play();
      }

      pauseAllGalleryVideos() {
        this.querySelectorAll('[data-gallery-video-item] video-element').forEach((videoEl) => {
          if (typeof videoEl.pause === 'function') videoEl.pause();
        });
      }

      updateRailPreview() {
        const railVideos = this.querySelectorAll('[data-video-rail] video');
        if (!railVideos.length) return;

        railVideos.forEach((video) => video.pause());

        if (FoxTheme.config.mqlMobile || this.galleryMode === 'video') return;

        const preview = railVideos[0];
        preview.muted = true;
        preview.playsInline = true;
        preview.play().catch(() => {});
      }

      setSliderOptions() {
        const mediaItemGap = parseInt(this.dataset.mediaItemGap);

        this.sliderOptions = {
          init: false,
          slidesPerView: '1',
          spaceBetween: mediaItemGap,
          loop: false,
          grabCursor: true,
          allowTouchMove: true,
          autoHeight: !(
            this.galleryMediaStyle === 'framed' &&
            (this.mediaLayout === 'carousel' || this.mediaLayout === 'vertical-carousel')
          ),
          navigation: {
            nextEl: this.querySelector('.slider-controls--group .swiper-button-next'),
            prevEl: this.querySelector('.slider-controls--group .swiper-button-prev'),
          },
          pagination: {
            el: this.querySelector('.slider-controls--group .swiper-pagination'),
            clickable: true,
            type: 'fraction',
          },
          threshold: 2,
        };

        this.thumbsOptions = {
          slidesPerView: 4,
          breakpoints: {
            461: {
              slidesPerView: 5,
            },
          },
          spaceBetween: mediaItemGap,
          loop: false,
          freeMode: true,
          watchOverflow: true,
          watchSlidesProgress: true,
          threshold: 2,
        };

        switch (this.mediaLayout) {
          case 'vertical-carousel':
            this.thumbsOptions = Object.assign({}, this.thumbsOptions, {
              breakpoints: {
                768: {
                  direction: 'vertical',
                  slidesPerView: 'auto',
                },
              },
            });
            break;
          case 'slider-freemode':
            this.sliderOptions = Object.assign({}, this.sliderOptions, {
              slidesPerView: 'auto',
            });
            break;
        }

        if (this.querySelector('[data-thumbs-next]') && this.mediaLayout !== 'vertical-carousel') {
          this.thumbsOptions = Object.assign({}, this.thumbsOptions, {
            slidesPerView: 'auto',
            spaceBetween: 12,
            freeMode: true,
            watchOverflow: true,
            navigation: {
              nextEl: this.querySelector('[data-thumbs-next]'),
              prevEl: this.querySelector('[data-thumbs-prev]'),
            },
            breakpoints: {
              461: {
                slidesPerView: 'auto',
              },
            },
          });
        }
      }

      updateMediaLayout() {
        if (FoxTheme.config.mqlMobile) {
          this.initSlider();
        } else {
          if (this.enableDesktopSlider) {
            this.initSlider();
          } else {
            this.destroySlider();
          }
        }
        this.updateRailPreview();
      }

      initSlider() {
        if (typeof this.sliderInstance !== 'object') {
          if ((this.enableDesktopSlider || this.enableMobileThumbnails) && this.elements.thumbnails) {
            this.thumbsInstance = new window.FoxTheme.Carousel(this.elements.thumbnails, this.thumbsOptions);
            this.thumbsInstance.init();

            this.sliderOptions.thumbs = {
              swiper: this.thumbsInstance.slider,
              autoScrollOffset: 2,
            };
          }

          this.sliderInstance = new window.FoxTheme.Carousel(this.elements.viewer, this.sliderOptions, [
            FoxTheme.Swiper.Thumbs,
          ]);
          this.sliderInstance.init();

          this.handleSliderAfterInit();
          this.handleSlideChange();

          this.sliderInstance.slider.init();
        }
      }

      destroySlider() {
        if (typeof this.sliderInstance === 'object') {
          this.sliderInstance.slider.destroy();
          this.sliderInstance = false;
        }
      }

      initThumbsSlider() {
        if (typeof this.thumbsInstance !== 'object') {
          this.thumbsInstance = new window.FoxTheme.Carousel(this.selectors.thumbnails, this.thumbsOptions);
          this.thumbsInstance.init();
        }
      }

      initImageZoom() {
        let dataSource = [];
        const allMedia = [...this.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)')];
        if (allMedia) {
          allMedia.forEach((media) => {
            switch (media.dataset.mediaType) {
              case 'model':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  html: `<div class="pswp__item--${media.dataset.mediaType}">${
                    media.querySelector('product-model').outerHTML
                  }</div>`,
                  mediaType: media.dataset.mediaType,
                });
                break;
              case 'video':
              case 'external_video':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  html: `<div class="pswp__item--${media.dataset.mediaType}">${
                    media.querySelector('video-element').outerHTML
                  }</div>`,
                  mediaType: media.dataset.mediaType,
                });
                break;
              case 'image':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  src: media.dataset.src,
                  width: media.dataset.pswpWidth,
                  height: media.dataset.pswpHeight,
                  mediaType: media.dataset.mediaType,
                });
                break;
            }
          });
        }

        this.lightbox = new window.FoxTheme.PhotoSwipeLightbox({
          dataSource: dataSource,
          pswpModule: window.FoxTheme.PhotoSwipe,
          bgOpacity: 1,
          arrowPrev: false,
          arrowNext: false,
          zoom: false,
          close: false,
          counter: false,
          preloader: false,
        });

        this.lightbox.addFilter('thumbEl', (thumbEl, { id }, index) => {
          if (this.sliderInstance && this.sliderInstance.slider) {
            const { slides, activeIndex } = this.sliderInstance.slider;
            if (slides[activeIndex]) {
              const el = slides[activeIndex].querySelector('img');
              if (el) {
                return el;
              }
            }
          }

          return thumbEl;
        });

        this.lightbox.addFilter('placeholderSrc', (placeholderSrc, { data: { id } }) => {
          if (this.sliderInstance && this.sliderInstance.slider) {
            const { slides, activeIndex } = this.sliderInstance.slider;
            if (slides[activeIndex]) {
              const el = slides[activeIndex].querySelector('img');
              if (el) {
                return el.src;
              }
            }
          }

          return placeholderSrc;
        });

        this.lightbox.on('change', () => {
          window.pauseAllMedia(this);
        });

        this.lightbox.on('destroy', () => {
          const { currIndex } = this.lightbox.pswp;

          if (this.sliderInstance && this.sliderInstance.slider) {
            this.sliderInstance.slider.slideToLoop(currIndex, 0, false);
          }
        });

        this.lightbox.on('pointerDown', (e) => {
          if (this.lightbox.pswp.currSlide.data.mediaType != 'image') {
            e.preventDefault();
          }
        });

        this.lightbox.on('uiRegister', () => {
          if (!this.onlyImage) {
            this.lightbox.pswp.ui.registerElement({
              name: 'next',
              ariaLabel: 'Next slide',
              order: 3,
              isButton: true,
              html: '<svg class="pswp-icon-next flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox.pswp.next();
              },
            });
            this.lightbox.pswp.ui.registerElement({
              name: 'prev',
              ariaLabel: 'Previous slide',
              order: 1,
              isButton: true,
              html: '<svg class="pswp-icon-prev rtl-flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox.pswp.prev();
              },
            });
          }
          this.lightbox.pswp.ui.registerElement({
            name: 'close-zoom',
            ariaLabel: 'Close zoom image',
            order: 2,
            isButton: true,
            html: '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>',
            onClick: (event, el) => {
              this.lightbox.pswp.close();
            },
          });
        });

        this.lightbox.init();

        FoxTheme.utils.addEventDelegate({
          selector: '.js-photoswipe--zoom',
          context: this,
          handler: (e, media) => {
            if (media.dataset.mediaType === 'image') {
              const index = Number(media.dataset.mediaIndex) || 0;
              this.lightbox.loadAndOpen(index);
            }
          },
        });
      }

      handleSliderAfterInit() {
        this.sliderInstance.slider.on('afterInit', (swiper) => {
          const { slides, activeIndex } = swiper;

          if (slides[activeIndex]) {
            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      handleSlideChange() {
        this.sliderInstance.slider.on('realIndexChange', (swiper) => {
          const { slides, activeIndex, thumbs } = swiper;

          if (this.galleryMode === 'video') {
            this.setGalleryMode('images');
          }

          if (thumbs.swiper) {
            thumbs.swiper.slideTo(activeIndex);
          }

          if (slides[activeIndex]) {
            this.playActiveMedia(slides[activeIndex]);

            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      toggleSliderDraggableState(isDraggable) {
        if (!this.sliderInstance || !this.sliderInstance.slider) return;
        if (this.sliderInstance.slider.allowTouchMove !== isDraggable) {
          this.sliderInstance.slider.allowTouchMove = isDraggable;
        }
      }

      playActiveMedia(selected) {
        const deferredMedia = selected.querySelector('product-model');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      setActiveMedia(variant) {
        if (!variant || !variant.hasOwnProperty('featured_media') || !variant.featured_media) return;

        const featuredMediaId = String(variant.featured_media.id);

        if (this.querySelector(`[data-gallery-video-item][data-video-id="${featuredMediaId}"]`)) {
          this.setGalleryMode('video', featuredMediaId);
          return;
        }

        this.setGalleryMode('images');

        if (this.sliderInstance && this.sliderInstance.slider) {
          const slides = this.sliderInstance.slider.slides;
          const slideIndex = Array.from(slides).findIndex((slide) => slide.dataset.mediaId === featuredMediaId);
          if (slideIndex >= 0) {
            this.sliderInstance.slider.slideTo(slideIndex, 0, false);
            return;
          }
        }

        this.sortMediaItems(variant);
      }

      sortMediaItems(variant) {
        let newMedias = this.elements.mediaItems;

        // Reset ordering.
        newMedias.sort(function (a, b) {
          return a.dataset.mediaIndex - b.dataset.mediaIndex;
        });

        newMedias.some((media, index) => {
          if (media.dataset.mediaId == variant.featured_media.id) {
            const [element] = newMedias.splice(index, 1);
            newMedias.unshift(element);
            return true;
          }
        });

        this.elements.mediaList.innerHTML = '';
        newMedias.forEach((media) => {
          this.elements.mediaList.appendChild(media);
        });

        if (!FoxTheme.config.mqlMobile) {
          const selectedMedia = this.querySelector(`[data-media-id="${variant.featured_media.id}"]`);
          if (selectedMedia) {
            window.scrollTo({ top: selectedMedia.offsetTop, behavior: 'smooth' });
          }
        }
      }
    }
  );
}
